import * as React from "react";
import { Checkbox, Tab, Tabs, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type { Device } from "../../../lib/apis/dtos/response/device.response";
import type { dpsQuotationObject } from "../../../lib/apis/dtos/request/quotation.request";
import type { DEVICE_CATEGORY_TYPE } from "../../../lib/apis/dtos/request/device.request";
import { useDeviceList } from "../../../lib/apis/hooks/device.hook";
import ConfirmModal from "../confirm.modal";
import type { ModalComponentProps } from "../../../types/modal.types";

interface GetDeviceResponse extends Device {
  type: "ADD" | "REMAIN" | "CHECK";
  storage: string;
  color: string;
}

interface DeviceSelectModalProps extends ModalComponentProps {
  title?: string;
  used?: dpsQuotationObject[];
  category?: DEVICE_CATEGORY_TYPE;
  onSelected?: (form: GetDeviceResponse[]) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const PHONE_TYPE = [
  { label: "갤럭시", value: "GALAXY" },
  { label: "아이폰", value: "IPHONE" },
  { label: "기타", value: "ETC" },
];

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      className="w-[376px] ml-[16px] p-[16px]"
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <div>{children}</div>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

export default function DpDeviceSelectModal(props: DeviceSelectModalProps) {
  const { id, onClose, used, onSelected, category = "ALL" } = props;

  const [select, setSelect] = React.useState<GetDeviceResponse[]>([]);
  const [search, setSearch] = React.useState("");

  const [value, setValue] = React.useState(0);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setSearch("");
  };

  const { data, isSuccess } = useQuery({
    ...useDeviceList({
      category: category,
      page: 0,
      size: 1000,
      defaultOnly: false,
    }),
  });
  const { result } = data || {};
  const { content = [] } = result || {}

  const hasEffectRun = React.useRef(false);

  React.useEffect(() => {
    if (isSuccess && !hasEffectRun.current) {
      let _push = [] as GetDeviceResponse[];
      (content || [])?.map((item) =>
        item.sizeAvailable.map((size) =>
          item.colorAndImage.map((color) => {
            _push.push({
              ...item,
              storage: size,
              color: color.color,
              type: "ADD",
            });
          })
        )
      );

      const compareIds = new Set(
        (used || [])?.map(
          (item) => `${item.deviceId}-${item.storage}-${item.color}`
        )
      );

      const merge = _push.map((item) => ({
        ...item,
        type: compareIds.has(
          `${item.deviceId}-${item.storage}-${item.color}`
        )
          ? "REMAIN"
          : ("ADD" as "REMAIN" | "ADD" | "CHECK"),
      }));

      setSelect(merge);
    }
  }, [isSuccess]);

  return (
    <ConfirmModal
      title={`${props.title} - 항목 추가`}
      rightButton={{
        title: `${select.filter(({ type }) => type == "CHECK").length > 0
          ? `${select.filter(({ type }) => type == "CHECK").length}개 항목 `
          : ""
          }추가`,
        disabled: select.filter(({ type }) => type == "CHECK").length == 0,
        onClick: () => {
          const _filter = select.filter(
            ({ type }) => type == "CHECK"
          ) as GetDeviceResponse[];
          onSelected?.(_filter);
          onClose?.();
        },
      }}
      id={id}
      onClose={onClose}
    >
      <div className="flex-row items-start h-[480px]">
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={value}
          onChange={handleChange}
          aria-label="Vertical tabs example"
          sx={{ borderRight: 1, borderColor: "divider" }}
        >
          {PHONE_TYPE.map(({ label }, index) => (
            <Tab key={index} label={label} {...a11yProps(index)} />
          ))}
        </Tabs>
        {PHONE_TYPE.map(({ value: _value }, index) => {
          return (
            <TabPanel key={index} value={value} index={index}>
              <TextField
                className="w-full"
                label="휴대폰 기종명"
                placeholder="휴대폰 기종명으로 검색"
                slotProps={{
                  inputLabel: { shrink: true },
                }}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
              <section className="h-[380px] overflow-y-auto">
                {isSuccess &&
                  (
                    (select || [])?.filter(
                      (item) =>
                        item.deviceName.includes(search) &&
                        item.category == PHONE_TYPE[value].value
                    ) || []
                  ).map((item, index) => {
                    // let _index = 0;
                    // select.filter((item, index) => {
                    //   if (item.deviceId === deviceId && item.storage == storage) {
                    //     _index = index;
                    //     return item;
                    //   }
                    // });
                    return (
                      <div
                        key={index}
                        className="mt-[8px] flex-row-between overflow-y-auto"
                      >
                        <div>
                          <Checkbox
                            // disabled={item.type == "REMAIN"}
                            checked={item.type == "CHECK"}
                            onClick={() => {
                              setSelect((prev) =>
                                prev.map((prevItem) =>
                                  prevItem.deviceId === item.deviceId &&
                                    prevItem.storage === item.storage &&
                                    prevItem.color === item.color
                                    ? {
                                      ...prevItem,
                                      type:
                                        prevItem.type == "CHECK"
                                          ? "ADD"
                                          : "CHECK",
                                    }
                                    : prevItem
                                )
                              );
                            }}
                          />
                          <span
                          // className={cn(
                          //   item.type !== "REMAIN"
                          //     ? "text-[#000000]"
                          //     : "text-[#000000]/[0.38]"
                          // )}
                          >
                            {/* {item.deviceName} */}
                            {`${item.deviceName} ${item.storage ? item.storage : ""} ${item.color ? item.color : ""
                              }`}
                          </span>
                        </div>
                        {/* {item.type == "REMAIN" && (
                        <span className="text-[#007AFF]">추가됨</span>
                      )} */}
                      </div>
                    );
                  })}
              </section>
            </TabPanel>
          );
        })}
      </div>
    </ConfirmModal>
  );
}
